import baseVariables from 'bitmovin-web-ui/src/styles/baseVariables';
import React from 'react';
import styled from 'styled-components';

import EnvironmentService from '../../logic/environmentService';
import {AppRoutePathFunction} from '../../logic/routes';
import BitmovinLogoPath from '../../media/bitmovin_logo_grayscale.svg';
import HeartIconPath from '../../media/heart_icon.svg';
import FacebookIconPath from '../../media/social_facebook.svg';
import InstagramIconPath from '../../media/social_instagram.svg';
import LinkedInIconPath from '../../media/social_linkedin.svg';
import TwitterIconPath from '../../media/social_twitter.svg';
import {Link, orangeColorRGB} from '../globalStyles';

//#region Styled Components

const Breakpoint = '88em';

const StyledFooter = styled.footer`
  font-size: 0.8em;
  padding: 3em;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-top: 4px solid ${orangeColorRGB};
  background-color: hsl(200, 100%, 99%);

  h2 {
    margin-top: 0;
    margin-bottom: 0.5em;
    font-size: 1em;
    font-weight: 500;
    letter-spacing: 0.125em;
    text-transform: uppercase;
  }

  a,
  span {
    margin-top: 1em;
    font-size: 1em;
    font-weight: normal;
  }
`;

const StyledFooterContent = styled.div`
  width: ${baseVariables.sizes.maxWidth};
  max-width: 100%;
  display: flex;
  flex-wrap: wrap;

  @media (max-width: ${Breakpoint}) {
    flex-direction: column;
    align-items: center;
  }
`;

const StyledNav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;

  @media (max-width: ${Breakpoint}) {
    justify-content: center;
  }
`;

const StyledNavigationBlock = styled.div`
  width: 26em;
  margin: 1em;
  padding: 1em 2em;
  display: flex;
  background-color: ${baseVariables.colors.lightBlueGray};
  border-radius: ${baseVariables.sizes.borderRadius};
  border-bottom: ${baseVariables.sizes.borderRadius} solid ${baseVariables.colors.mediumBlueGray};
`;

const StyledNavigationSection = styled.div`
  display: flex;
  flex-direction: column;

  &:not(:last-child) {
    margin-right: 5em;
  }
`;

const StyledAnchor = styled.a`
  color: ${baseVariables.colors.midnight};
`;

const StyledEmptyHeadline = styled.div`
  margin-top: 1.65em;
`;

const StyledAddress = styled.address`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  text-align: right;

  h2 {
    margin-top: 1em;
  }

  @media (max-width: ${Breakpoint}) {
    width: 100%;
    text-align: center;
    align-items: center;
  }
`;

const StyledSocialMediaIcons = styled.div`
  margin-top: 1em;
  display: flex;
  justify-content: flex-end;
`;

const StyledSocialMediaIcon = styled.div`
  width: 2em;
  height: 2em;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${baseVariables.colors.lightBlueGray};
  border-radius: 100%;
  margin-right: 1em;

  &:last-child {
    margin-right: 0;
  }

  > a {
    margin-top: 0;
  }

  > a > img {
    height: 1em;
  }
`;

const StyledCopyrightLine = styled.div`
  width: ${baseVariables.sizes.maxWidth};
  max-width: 100%;
  display: flex;
  flex-direction: column;

  > hr {
    margin-left: 1em;
    margin-top: 0.5em;
    margin-bottom: 1em;
  }
`;

const StyledCopyrightLineContent = styled.div`
  display: flex;
  color: ${baseVariables.colors.withOpacity(baseVariables.colors.dusk, 62)};
  line-height: 1.5;

  a {
    margin: 0;
    display: inline-block;
    color: ${baseVariables.colors.withOpacity(baseVariables.colors.dusk, 62)};
  }

  > span {
    margin: 0 2em 0 1em;
  }

  > p {
    margin: 0;
    flex-grow: 1;

    > span {
      margin: 0 1em;
    }
  }

  > img {
    height: 1.5em;
  }

  @media (max-width: ${Breakpoint}) {
    flex-direction: column;
    align-items: center;

    > span {
      order: 2;
      margin-top: 3em;
      text-align: center;
    }

    > p {
      text-align: center;
    }

    > img {
      display: none;
    }
  }
`;

const StyledLink = styled(Link)`
  color: ${baseVariables.colors.midnight};
`;

//#endregion

const Footer = () => (
  <StyledFooter>
    <StyledFooterContent>
      <StyledNav>
        <StyledNavigationBlock>
          <StyledNavigationSection>
            <h2>Products</h2>
            <StyledAnchor href="https://bitmovin.com/encoding-service/" target="_blank">
              Encoding
            </StyledAnchor>
            <StyledAnchor href="https://bitmovin.com/video-player" target="_blank">
              Player
            </StyledAnchor>
            <StyledAnchor href="https://bitmovin.com/video-analytics" target="_blank">
              Analytics
            </StyledAnchor>
          </StyledNavigationSection>
          <StyledNavigationSection>
            <h2>News + Events</h2>
            <StyledAnchor href="https://bitmovin.com/blog" target="_blank">
              Blog
            </StyledAnchor>
            <StyledAnchor href="https://bitmovin.com/press-room" target="_blank">
              Newsroom
            </StyledAnchor>
            <StyledAnchor href="https://bitmovin.com/press-room/#logoDownload" target="_blank">
              Brand Kit
            </StyledAnchor>
          </StyledNavigationSection>
        </StyledNavigationBlock>
        <StyledNavigationBlock>
          <StyledNavigationSection>
            <h2>Company</h2>
            <StyledLink to={AppRoutePathFunction.AboutUs()}>About</StyledLink>
            <StyledAnchor href="https://bitmovin.com/pricing" target="_blank">
              Pricing
            </StyledAnchor>
            <StyledAnchor href="https://bitmovin.com/careers" target="_blank">
              Careers
            </StyledAnchor>
          </StyledNavigationSection>
          <StyledNavigationSection>
            <StyledEmptyHeadline />
            <StyledAnchor href="https://bitmovin.com/partners" target="_blank">
              Partners
            </StyledAnchor>
            <StyledAnchor href="https://bitmovin.com/contact" target="_blank">
              Contact
            </StyledAnchor>
            <StyledAnchor href="https://bitmovin.com/awards-and-memberships" target="_blank">
              Awards & Memberships
            </StyledAnchor>
          </StyledNavigationSection>
        </StyledNavigationBlock>
        <StyledNavigationBlock>
          <StyledNavigationSection>
            <h2>Support</h2>
            <StyledAnchor href="https://bitmovin.com/docs/encoding" target="_blank">
              Encoding
            </StyledAnchor>
            <StyledAnchor href="https://bitmovin.com/docs/player" target="_blank">
              Player
            </StyledAnchor>
            <StyledAnchor href="https://bitmovin.com/docs/analytics" target="_blank">
              Analytics
            </StyledAnchor>
          </StyledNavigationSection>
          <StyledNavigationSection>
            <h2>Development</h2>
            <StyledAnchor href="https://github.com/bitmovin" target="_blank">
              GitHub
            </StyledAnchor>
            <StyledAnchor href="https://bitmovin.com/dashboard" target="_blank">
              Bitmovin Dashboard
            </StyledAnchor>
          </StyledNavigationSection>
        </StyledNavigationBlock>
      </StyledNav>
      <StyledAddress>
        <h2>Bitmovin Inc</h2>
        <span>41 Drumm Street</span>
        <span>San Francisco | CA 94111 | USA</span>
        <span>+1 (800) 690-1826</span>
        <h2>Follow us on social media</h2>
        <StyledSocialMediaIcons>
          <StyledSocialMediaIcon>
            <a href="https://www.facebook.com/bitmovin/" target="_blank">
              <img src={FacebookIconPath} alt="Facebook" />
            </a>
          </StyledSocialMediaIcon>
          <StyledSocialMediaIcon>
            <a href="https://twitter.com/bitmovin" target="_blank">
              <img src={TwitterIconPath} alt="Twitter" />
            </a>
          </StyledSocialMediaIcon>
          <StyledSocialMediaIcon>
            <a href="https://www.linkedin.com/company/bitmovin" target="_blank">
              <img src={LinkedInIconPath} alt="LinkedIn" />
            </a>
          </StyledSocialMediaIcon>
          <StyledSocialMediaIcon>
            <a href="https://www.instagram.com/bitmovin/" target="_blank">
              <img src={InstagramIconPath} alt="Instagram" />
            </a>
          </StyledSocialMediaIcon>
        </StyledSocialMediaIcons>
      </StyledAddress>
    </StyledFooterContent>
    <StyledCopyrightLine>
      <StyledCopyrightLineContent>
        <span>
          Made with <img src={HeartIconPath} alt="love" /> in • San Francisco • Denver • Klagenfurt • Vienna • Berlin •
          London • Sao Paulo • Guadalajara • Mexico City • Dubai • Singapore • Tokyo • Sydney
        </span>
      </StyledCopyrightLineContent>
      <hr />
      <StyledCopyrightLineContent>
        <span>Copyright &copy; {new Date().getFullYear()} Bitmovin Inc. All rights reserved</span>
        <p>
          <a href="https://bitmovin.com/privacy/" target="_blank">
            Privacy Policy
          </a>
          <span>|</span>
          <a href="https://bitmovin.com/terms/" target="_blank">
            Terms of Service
          </a>
        </p>
        {EnvironmentService.BRANCH && <span>Branch:&nbsp;{EnvironmentService.BRANCH}</span>}
        {EnvironmentService.VERSION && <span>Version:&nbsp;{EnvironmentService.VERSION}</span>}

        <img src={BitmovinLogoPath} alt="BitmovinLogo" />
      </StyledCopyrightLineContent>
    </StyledCopyrightLine>
  </StyledFooter>
);

export default Footer;
